import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import z from 'zod';

import {
  DocumentType,
  GetAllDocumentsQuery,
  GetAllDocumentsQueryVariables,
  GET_ALL_TAX_DOCUMENTS,
  TaxAuthority,
  useCurrentCompanyId,
  DocumentStatus,
  TaxDocument,
} from '@neo.tax/shared';

import { useRdContext } from '../context';

export interface UseTaxDocuments {
  taxDocuments: Array<TaxDocument>;
  loading: boolean;
}

/**
 * @deprecated use {@link useGetDocuments} instead.
 */
export const useAllTaxDocuments = (): UseTaxDocuments => {
  const companyId = useCurrentCompanyId();
  const { fiscalYear } = useRdContext();

  const { data, loading } = useQuery<GetAllDocumentsQuery, GetAllDocumentsQueryVariables>(GET_ALL_TAX_DOCUMENTS, {
    variables: { companyId },
    fetchPolicy: 'network-only',
  });

  const taxDocuments = useMemo(() => {
    const documentsForFiscalYear = data?.getAllDocuments.find((documents) => documents.id === fiscalYear.id);
    if (!documentsForFiscalYear) {
      return [];
    }
    const { federalRdTaxCreditDocuments, stateRdTaxCreditDocuments } = documentsForFiscalYear;
    return [
      {
        fiscalYearId: fiscalYear.id,
        taxYear: fiscalYear.year,
        fileName: federalRdTaxCreditDocuments?.taxCreditFormFileName,
        url: federalRdTaxCreditDocuments?.taxCreditFormUrl,
        status: federalRdTaxCreditDocuments?.taxCreditFormStatus,
        missingInformation: federalRdTaxCreditDocuments?.taxWriteUpMissingInformation,
        generatedDate: federalRdTaxCreditDocuments?.taxCreditFormGeneratedDate,
        documentType: DocumentType.RdCreditAndCapitalizationWithInstructions,
        taxAuthority: TaxAuthority.Federal,
      },
      {
        fiscalYearId: fiscalYear.id,
        taxYear: fiscalYear.year,
        fileName: federalRdTaxCreditDocuments?.taxWriteUpPdfFileName,
        url: federalRdTaxCreditDocuments?.taxWriteUpPdfUrl,
        status: federalRdTaxCreditDocuments?.taxWriteUpStatus,
        missingInformation: federalRdTaxCreditDocuments?.taxWriteUpMissingInformation,
        generatedDate: federalRdTaxCreditDocuments?.taxWriteUpGeneratedDate,
        documentType: DocumentType.WriteUp,
        taxAuthority: TaxAuthority.Federal,
      },
      ...stateRdTaxCreditDocuments.map((stateRdTaxCreditDocument) => ({
        fiscalYearId: fiscalYear.id,
        taxYear: fiscalYear.year,
        fileName: stateRdTaxCreditDocument?.stateTaxCreditFormFileName,
        url: stateRdTaxCreditDocument?.stateTaxCreditFormUrl,
        status: stateRdTaxCreditDocument?.stateTaxCreditFormUrl
          ? DocumentStatus.ReadyForDownload
          : DocumentStatus.HumanReview,
        documentType: DocumentType.RdCreditWithInstructions,
        taxAuthority: z.enum(TaxAuthority).parse(stateRdTaxCreditDocument.state),
      })),
    ];
  }, [data?.getAllDocuments, fiscalYear.id, fiscalYear.year]);

  return { taxDocuments, loading };
};
